// ============================================================
// avatarPage.js — Página "Generación de avatar"
// ------------------------------------------------------------
// - Pestañas de modo (Guion a vídeo / Foto a vídeo)
// - Selector de modelo de avatar (dropdown)
// - Selector de calidad (dropdown)
// - Selector de voz (panel lateral deslizante)
// - Selección de avatar en el grid
// - Botón Generar
// ============================================================
import { qs, qsa, on } from './utils.js';
import { toast } from './toast.js';
import { getUploadsUrl, uploadAttachment, apiRequest, generateAvatar, listHistory, getJobStatus } from './api.js';

export function initAvatarPage() {
  if (document.body.dataset.page !== 'avatar') return;

  let clonameUploadedFile = null;

  function saveCustomAvatarToStorage(url, name, isVideo) {
    try {
      const saved = JSON.parse(localStorage.getItem('vimo_custom_avatars') || '[]');
      if (!saved.some(a => a.url === url)) {
        saved.unshift({ url, name, isVideo });
        localStorage.setItem('vimo_custom_avatars', JSON.stringify(saved));
      }
    } catch (e) {
      console.error('Error al guardar avatar en storage:', e);
    }
  }

  function addNewAvatarToGrid(url, name, isVideo, select = true) {
    const grid = qs('.avatar-grid');
    if (!grid) return;

    if (select) {
      // Deseleccionar el resto si se solicita selección
      qsa('.av-thumb-card').forEach(c => c.classList.remove('is-selected'));
    }

    // Guardar en localStorage para persistencia al navegar entre páginas
    saveCustomAvatarToStorage(url, name, isVideo);

    const newCard = document.createElement('button');
    newCard.className = `av-thumb-card ${select ? 'is-selected' : ''}`;
    newCard.type = 'button';
    newCard.dataset.avatar = url;

    const shortName = name.length > 15 ? name.slice(0, 15) + '…' : name;

    if (isVideo) {
      newCard.innerHTML = `
        <video class="av-thumb-img" src="${url}" poster="${url}" autoplay loop muted playsinline style="width:100%; height:100%; object-fit:cover; object-position: top center;"></video>
        <span class="av-thumb-name">${shortName}</span>
      `;
    } else {
      newCard.innerHTML = `
        <img class="av-thumb-img" src="${url}" alt="${name}" loading="lazy">
        <span class="av-thumb-name">${shortName}</span>
      `;
    }

    // Insertar después de las 3 tarjetas de acción
    const actionCards = qsa('.av-action-card', grid);
    if (actionCards.length > 0) {
      const lastActionCard = actionCards[actionCards.length - 1];
      lastActionCard.after(newCard);
    } else {
      grid.prepend(newCard);
    }
  }

  // Cargar avatares personalizados guardados en visitas anteriores
  try {
    const saved = JSON.parse(localStorage.getItem('vimo_custom_avatars') || '[]');
    saved.reverse().forEach(item => {
      addNewAvatarToGrid(item.url, item.name, item.isVideo, false);
    });
  } catch (e) {}

  // Corregir URLs de avatares predefinidos dinámicamente para soportar producción
  const uploadsUrl = getUploadsUrl();
  qsa('.av-thumb-img').forEach(el => {
    const src = el.getAttribute('src');
    if (src && src.includes('localhost:4001/uploads')) {
      const filename = src.substring(src.lastIndexOf('/') + 1);
      el.src = `${uploadsUrl}/${filename}`;
    }
    const poster = el.getAttribute('poster');
    if (poster && poster.includes('localhost:4001/uploads')) {
      const posterName = poster.substring(poster.lastIndexOf('/') + 1);
      el.setAttribute('poster', `${uploadsUrl}/${posterName}`);
    }
  });

  // ── Referencias ──────────────────────────────────────────
  const voiceModal      = qs('#voiceModal');
  const createVoiceModal = qs('#createVoiceModal');
  const audioModal      = qs('#audioModal');
  const audioModalTitle = qs('#audioModalTitle');
  const cloneModal      = qs('#cloneModal');
  const clonamodalRoot  = qs('#clonamodalRoot');
  const scriptArea      = qs('#avatarScript');
  const scriptCount     = qs('#scriptCount');
  const selectVoiceBtn  = qs('[data-action="select-voice"]');

  // ── Pestañas de modo ─────────────────────────────────────
  const avModelDropup = qs('#avModelDropup');

  on(document, 'click', '[data-action="av-tab"]', (e, btn) => {
    const tab = btn.dataset.tab;   // 'guion' | 'foto'
    qsa('.avatar-tab').forEach(t => t.classList.remove('is-active'));
    btn.classList.add('is-active');

    // Cambia la vista del panel derecho
    const stageKey = tab === 'foto' ? 'foto' : 'avatares';
    qsa('.av-stage-view').forEach(v => {
      v.classList.toggle('is-hidden', v.dataset.stage !== stageKey);
    });

    // En modo foto ocultamos el selector de modelo de avatar
    if (avModelDropup) avModelDropup.classList.toggle('is-hidden', tab === 'foto');
  });

  // ── Drop zone foto a vídeo ────────────────────────────────
  const avFotoDrop        = qs('#avFotoDrop');
  const avFotoDropEmpty   = qs('#avFotoDropEmpty');
  const avFotoPreviewBox  = qs('#avFotoPreviewBox');
  const avFotoPreviewImg  = qs('#avFotoPreviewImg');
  const avFotoFileName    = qs('#avFotoFileName');
  let currentUploadedPhotoFile = null;

  async function setUploadedPhoto(file) {
    if (!file) return;
    currentUploadedPhotoFile = file;

    const reader = new FileReader();
    reader.onload = (e) => {
      if (avFotoPreviewImg) avFotoPreviewImg.src = e.target.result;
      if (avFotoFileName) avFotoFileName.textContent = file.name;
      if (avFotoDropEmpty) avFotoDropEmpty.classList.add('is-hidden');
      if (avFotoPreviewBox) avFotoPreviewBox.classList.remove('is-hidden');
      if (avFotoDrop) avFotoDrop.classList.add('has-photo');
    };
    reader.readAsDataURL(file);

    try {
      const uploadRes = await uploadAttachment(file);
      currentUploadedPhotoFile._url = uploadRes.url;
      toast({ message: `Foto "${file.name}" subida correctamente`, type: 'success' });
    } catch (err) {
      console.error('Error enviando archivo al servidor:', err);
      toast({ message: `Foto "${file.name}" lista`, type: 'success' });
    }
  }

  function clearUploadedPhoto() {
    currentUploadedPhotoFile = null;
    if (avFotoPreviewImg) avFotoPreviewImg.src = '';
    if (avFotoFileName) avFotoFileName.textContent = '';
    if (avFotoDropEmpty) avFotoDropEmpty.classList.remove('is-hidden');
    if (avFotoPreviewBox) avFotoPreviewBox.classList.add('is-hidden');
    if (avFotoDrop) avFotoDrop.classList.remove('has-photo');
    toast({ message: 'Foto eliminada', type: 'info' });
  }

  if (avFotoDrop) {
    avFotoDrop.addEventListener('click', (e) => {
      if (e.target.closest('.av-foto-btn-action')) return;
      if (!currentUploadedPhotoFile) pickAvatarFoto();
    });
    avFotoDrop.addEventListener('dragover', (e) => {
      e.preventDefault();
      avFotoDrop.classList.add('is-dragover');
    });
    avFotoDrop.addEventListener('dragleave', () => avFotoDrop.classList.remove('is-dragover'));
    avFotoDrop.addEventListener('drop', (e) => {
      e.preventDefault();
      avFotoDrop.classList.remove('is-dragover');
      const file = e.dataTransfer.files?.[0];
      if (file && file.type.startsWith('image/')) {
        setUploadedPhoto(file);
      } else if (file) {
        toast({ message: 'Por favor sube un archivo de imagen (PNG, JPG, WebP)', type: 'error' });
      }
    });
  }

  on(document, 'click', '#btnChangeFoto', (e) => {
    e.stopPropagation();
    pickAvatarFoto();
  });

  on(document, 'click', '#btnRemoveFoto', (e) => {
    e.stopPropagation();
    clearUploadedPhoto();
  });

  // ── Botón Generar Vídeo de Avatar ─────────────────────────
  let genCountNum = 2;

  on(document, 'click', '[data-action="generate-avatar"]', async (e) => {
    const btn = e.target.closest('[data-action="generate-avatar"]') || qs('[data-action="generate-avatar"]');
    const activeTab = qs('.avatar-tab.is-active')?.dataset.tab;
    const scriptText = scriptArea?.value.trim() || 'Guion de presentación para el avatar';

    if (activeTab === 'foto') {
      if (!currentUploadedPhotoFile) {
        toast({ message: 'Por favor sube primero una foto para crear el avatar', type: 'warning' });
        return;
      }

      genCountNum++;
      toast({ message: `🚀 Enviando foto a la IA... esto puede tardar unos segundos`, type: 'success' });

      // Deshabilitar botón mientras procesa
      btn.disabled = true;
      btn.textContent = 'Generando...';

      try {
        // Enviar a la API del backend — se espera respuesta para confirmar guardado en BD
        const result = await generateAvatar({
          text: scriptText,
          attachments: [currentUploadedPhotoFile],
          voiceId: selectedVoiceId,
        });

        if (result && (result.jobId || result.id)) {
          toast({ message: '✅ Avatar enviado a la IA. Verás el progreso aquí abajo.', type: 'success' });
          // Recargar la lista desde BD para mostrar la generación real
          await loadAvatarGenerations();
          // Scroll suave a la lista
          genList?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          toast({ message: '⚠️ Respuesta inesperada del servidor', type: 'warning' });
        }
      } catch (err) {
        console.error('[AVATAR] Error al generar:', err);
        toast({ message: `Error al generar: ${err.message}`, type: 'error' });
      } finally {
        btn.disabled = false;
        btn.textContent = 'Generar Avatar';
      }
    } else {
      toast({ message: '🚀 Generando vídeo de avatar sintético...', type: 'success' });
      btn.disabled = true;
      btn.textContent = 'Generando...';
      try {
        const selectedAvatarCard = qs('.av-thumb-card.is-selected');
        const selectedAvatarId = selectedAvatarCard?.dataset.avatar || 'demo_woman';
        const result = await generateAvatar({
          text: scriptText,
          avatarId: selectedAvatarId,
          voiceId: selectedVoiceId,
        });
        if (result && (result.jobId || result.id)) {
          toast({ message: '✅ Avatar enviado. Verás el progreso aquí abajo.', type: 'success' });
          await loadAvatarGenerations();
          genList?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch (err) {
        console.error('[AVATAR] Error al generar avatar:', err);
        toast({ message: `Error al generar avatar: ${err.message}`, type: 'error' });
      } finally {
        btn.disabled = false;
        btn.textContent = 'Generar Avatar';
      }
    }
  });

  // ── Gestión persistente de tarjetas eliminadas ──────────────
  function getDeletedCardIds() {
    try {
      return JSON.parse(localStorage.getItem('vimo_deleted_generations') || '[]');
    } catch {
      return [];
    }
  }

  function markCardAsDeleted(id) {
    if (!id) return;
    const deleted = getDeletedCardIds();
    if (!deleted.includes(id)) {
      deleted.push(id);
      localStorage.setItem('vimo_deleted_generations', JSON.stringify(deleted));
    }
    apiRequest(`/generations/${id}`, { method: 'DELETE' }).catch(() => {});
  }

  // ── Referencia a la lista de generaciones y a la función constructora de tarjetas ──
  const genList = qs('#avFotoGenerationsList');
  const countEl = qs('#avGenCount');

  // Construye el elemento HTML de una tarjeta de generación de avatar
  function buildAvatarCard(gen) {
    const card = document.createElement('div');
    card.className = 'av-gen-card';
    card.dataset.id = gen.id || '';

    // Poster: para tarjetas en curso mostramos la foto que subió el usuario
    const userPhotoThumb = gen.thumb || gen.meta?.photoUrl || '';
    const videoUrl = gen.url || gen.resultUrl || '';
    const isCompleted = gen.status === 'completed';
    const isQueued = gen.status === 'queued' || gen.status === 'processing';

    // Solo mostrar el vídeo si está completado Y tiene URL de vídeo real (mp4/webm/fal-cdn)
    const hasRealVideo = isCompleted && videoUrl && (videoUrl.includes('.mp4') || videoUrl.includes('.webm') || videoUrl.includes('fal.media') || videoUrl.includes('cdn'));
    const mediaHtml = hasRealVideo
      ? `<video class="av-gen-video" src="${videoUrl}" poster="${userPhotoThumb || uploadsUrl + '/demo_avatar_woman.png'}" preload="metadata" playsinline></video>`
      : `<img class="av-gen-video" src="${userPhotoThumb || (uploadsUrl + '/demo_avatar_woman.png')}" alt="Avatar" style="object-fit:cover;width:100%;height:100%;border-radius:8px 8px 0 0;">`;

    const badgeHtml = isCompleted && hasRealVideo
      ? `<span class="av-gen-badge-status"><i class="bi bi-check2-circle"></i> Completado</span>`
      : isQueued || (isCompleted && !hasRealVideo)
        ? `<span class="av-gen-badge-status" style="background:#b45309"><i class="bi bi-hourglass-split"></i> Procesando...</span>`
        : `<span class="av-gen-badge-status" style="background:#991b1b"><i class="bi bi-x-circle"></i> Error</span>`;

    const dateLabel = gen.createdAt ? new Date(gen.createdAt).toLocaleString('es-ES', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : 'Ahora mismo';
    const promptText = gen.prompt || gen.title || '';

    card.dataset.jobId = gen.jobId || '';
    card.dataset.status = gen.status;

    card.innerHTML = `
      <div class="av-gen-media">
        ${mediaHtml}
        ${hasRealVideo ? `<button class="av-gen-play-btn" type="button" aria-label="Reproducir"><i class="bi bi-play-fill"></i></button>` : ''}
        ${badgeHtml}
        <span class="av-gen-duration">00:18</span>
      </div>
      <div class="av-gen-details">
        <h4 class="av-gen-name">Avatar — ${promptText.slice(0, 30) || 'Sin guión'}</h4>
        <p class="av-gen-script">"${promptText.slice(0, 80)}${promptText.length > 80 ? '...' : ''}"</p>
        <div class="av-gen-foot">
          <span class="av-gen-date"><i class="bi bi-clock"></i> ${dateLabel}</span>
          <div class="av-gen-actions">
            <button class="av-gen-act-btn btn-dl" type="button" title="Descargar MP4">
              <i class="bi bi-download"></i>
            </button>
            <button class="av-gen-act-btn btn-del" type="button" title="Eliminar">
              <i class="bi bi-trash3"></i>
            </button>
          </div>
        </div>
      </div>
    `;
    return card;
  }

  // Polling activo para tarjetas en curso
  const pollingIntervals = new Map(); // genId → intervalId

  function startPollingCard(gen) {
    if (pollingIntervals.has(gen.id)) return; // ya haciendo polling
    const jobId = gen.jobId;
    if (!jobId) return;

    console.log(`[AVATAR POLL] Iniciando polling para job ${jobId}`);
    const intervalId = setInterval(async () => {
      try {
        const jobStatus = await getJobStatus(jobId);
        console.log(`[AVATAR POLL] Job ${jobId}: status=${jobStatus.status}, url=${jobStatus.result?.url}`);

        if (jobStatus.status === 'completed' || jobStatus.status === 'failed') {
          clearInterval(intervalId);
          pollingIntervals.delete(gen.id);
          // Recargar toda la lista para mostrar el resultado final
          await loadAvatarGenerations();
        }
      } catch (err) {
        console.warn(`[AVATAR POLL] Error polling job ${jobId}:`, err.message);
      }
    }, 10000); // cada 10 segundos

    pollingIntervals.set(gen.id, intervalId);
  }

  async function loadAvatarGenerations() {
    if (!genList) return;
    // Detener todos los pollings previos
    pollingIntervals.forEach((id) => clearInterval(id));
    pollingIntervals.clear();

    try {
      const data = await listHistory();
      const items = Array.isArray(data) ? data : (data?.generations || data?.items || []);
      const avatarItems = items.filter(g => g.type === 'avatar');
      const deletedIds = getDeletedCardIds();

      // Limpiar cards anteriores
      genList.innerHTML = '';

      if (avatarItems.length === 0) {
        genList.innerHTML = '<p style="color:var(--text-muted,#888);padding:1rem;text-align:center">Aún no has creado ningún avatar. ¡Sube una foto y genera tu primer vídeo!</p>';
        if (countEl) countEl.textContent = '0 vídeos';
        return;
      }

      let count = 0;
      for (const gen of avatarItems) {
        if (deletedIds.includes(gen.id)) continue;
        const card = buildAvatarCard(gen);
        genList.appendChild(card);
        count++;
        // Si está en proceso, iniciar polling
        if (gen.status === 'queued' || gen.status === 'processing') {
          startPollingCard(gen);
        }
      }
      if (countEl) countEl.textContent = `${count} vídeos`;
    } catch (err) {
      console.warn('[AVATAR] Error cargando generaciones:', err.message);
    }
  }

    // Cargar generaciones al iniciar la página
  loadAvatarGenerations();

  // Acciones en tarjetas de generaciones recientes
  on(document, 'click', '.av-gen-act-btn.btn-del', (e, btn) => {
    const card = btn.closest('.av-gen-card');
    if (card) {
      const cardId = card.dataset.id;
      if (cardId) markCardAsDeleted(cardId);
      card.remove();
      toast({ message: 'Generación eliminada', type: 'info' });
      const list = qs('#avFotoGenerationsList');
      const remaining = qsa('.av-gen-card', list).length;
      const countEl = qs('#avGenCount');
      if (countEl) countEl.textContent = `${remaining} vídeos`;
    }
  });

  on(document, 'click', '.av-gen-act-btn.btn-dl', (e, btn) => {
    e.stopPropagation();
    const card = btn.closest('.av-gen-card');
    const video = qs('video', card);
    const videoUrl = video?.src || `${uploadsUrl}/demo_avatar_woman.webm`;
    const link = document.createElement('a');
    link.href = videoUrl;
    link.download = `vimo-avatar-${Date.now()}.mp4`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast({ message: 'Descargando vídeo MP4...', type: 'success' });
  });

  // Reproducir/pausar vídeos reales en las tarjetas
  on(document, 'click', '.av-gen-play-btn, .av-gen-media', (e, target) => {
    if (e.target.closest('.av-gen-actions')) return;

    const mediaWrap = target.closest('.av-gen-media');
    if (!mediaWrap) return;

    let video = qs('video', mediaWrap);
    const playBtnIcon = qs('.av-gen-play-btn i', mediaWrap);

    if (!video) {
      const img = qs('img', mediaWrap);
      if (img) {
        video = document.createElement('video');
        video.className = 'av-gen-video';
        video.src = `${uploadsUrl}/demo_avatar_woman.webm`;
        video.poster = img.src;
        video.preload = 'metadata';
        video.playsInline = true;
        img.replaceWith(video);
      }
    }

    if (!video) return;

    // Pausar cualquier otro vídeo
    qsa('video.av-gen-video').forEach(otherVideo => {
      if (otherVideo !== video && !otherVideo.paused) {
        otherVideo.pause();
        const otherIcon = qs('.av-gen-play-btn i', otherVideo.closest('.av-gen-media'));
        if (otherIcon) otherIcon.className = 'bi bi-play-fill';
      }
    });

    if (video.paused) {
      video.play().then(() => {
        if (playBtnIcon) playBtnIcon.className = 'bi bi-pause-fill';
        toast({ message: 'Reproduciendo vídeo de avatar...', type: 'info' });
      }).catch(err => {
        console.error('Error playing video:', err);
        toast({ message: 'No se pudo reproducir el vídeo', type: 'error' });
      });
    } else {
      video.pause();
      if (playBtnIcon) playBtnIcon.className = 'bi bi-play-fill';
    }
  });

  function pickAvatarFoto() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.addEventListener('change', (ev) => {
      const file = ev.target.files?.[0];
      if (file) setUploadedPhoto(file);
    });
    input.click();
  }

  // ── Contador de caracteres (formato X/840) ────────────────
  const MAX_CHARS = 840;

  function updateCount() {
    if (scriptCount) scriptCount.textContent = scriptArea?.value.length ?? 0;
  }

  scriptArea?.addEventListener('input', updateCount);

  // ── Posicionamiento del tooltip de la bombilla ────────────
  const hintWrap    = qs('.avatar-hint-wrap');
  const hintTooltip = qs('.avatar-hint-tooltip');

  if (hintWrap && hintTooltip) {
    hintWrap.addEventListener('mouseenter', () => {
      const rect = hintWrap.getBoundingClientRect();
      hintTooltip.style.left = `${rect.left}px`;
      hintTooltip.style.top  = `${rect.top - hintTooltip.offsetHeight - 10}px`;
    });
    // Recalcula si el tooltip aún no tiene altura en el primer hover
    hintWrap.addEventListener('mouseover', () => {
      const rect = hintWrap.getBoundingClientRect();
      hintTooltip.style.left = `${rect.left}px`;
      hintTooltip.style.top  = `${rect.top - hintTooltip.offsetHeight - 10}px`;
    });
  }

  // ── Guion de muestra ──────────────────────────────────────
  const SAMPLE_SCRIPT = `Nunca dejes que el miedo al fracaso te impida intentarlo, porque cada intento te acerca un paso más a tu próxima gran victoria.

Llevamos años queriendo compartir lo que sabemos. Enseñar. Inspirar. Ayudar a otros a crecer más rápido.

Pero crear contenido de calidad lleva una eternidad. Grabar. Editar. Repetir. Es agotador.

Hasta que descubrí una forma diferente de hacerlo.

Con inteligencia artificial, puedo convertir un guion en un vídeo profesional en minutos, sin estudio, sin cámaras, sin complicaciones.

Puedo hablar en cualquier idioma, actualizar mi mensaje al instante y llegar a más personas que nunca.

¿Y lo mejor? Mi voz, mi imagen, mi esencia — siempre presentes.`;

  on(document, 'click', '[data-action="insert-sample-script"]', () => {
    if (!scriptArea) return;
    scriptArea.value = SAMPLE_SCRIPT;
    updateCount();
    scriptArea.focus();
  });

  // ── Selección de avatar y reproducción/pausa de vista previa ─────────
  // Pausar cualquier vídeo de avatar al iniciar la página
  qsa('.av-thumb-card video').forEach(v => {
    try {
      v.pause();
      v.currentTime = 0;
    } catch {}
  });

  on(document, 'click', '.av-thumb-card', (e, card) => {
    qsa('.av-thumb-card').forEach(c => c.classList.remove('is-selected'));
    card.classList.add('is-selected');

    const video = qs('video', card);
    const playOverlayIcon = qs('.av-thumb-play-overlay i', card);

    if (video) {
      // Pausar otros vídeos de las miniatura de avatares
      qsa('.av-thumb-card video').forEach(otherVideo => {
        if (otherVideo !== video && !otherVideo.paused) {
          otherVideo.pause();
          const otherIcon = qs('.av-thumb-play-overlay i', otherVideo.closest('.av-thumb-card'));
          if (otherIcon) otherIcon.className = 'bi bi-play-fill';
        }
      });

      if (video.paused) {
        video.play().then(() => {
          if (playOverlayIcon) playOverlayIcon.className = 'bi bi-pause-fill';
          toast({ message: 'Reproduciendo avatar...', type: 'info' });
        }).catch(err => {
          console.error('Error playing avatar preview video:', err);
        });
      } else {
        video.pause();
        if (playOverlayIcon) playOverlayIcon.className = 'bi bi-play-fill';
      }
    }
  });

  // ── Selector de modelo ────────────────────────────────────
  on(document, 'click', '.av-model-item', (e, item) => {
    qsa('.av-model-item').forEach(i => i.classList.remove('is-active'));
    item.classList.add('is-active');

    const badge = item.dataset.badge;
    const name  = qs('strong', item)?.textContent || 'Avatar';
    const isNew = item.dataset.new === 'true';

    const badgeEl = qs('[data-bind="av-model-badge"]');
    const nameEl  = qs('[data-bind="av-model-name"]');
    const newBadge = qs('.avatar-pill .badge-new');

    if (badgeEl) badgeEl.textContent = badge;
    if (nameEl)  nameEl.textContent  = name;
    if (newBadge) newBadge.style.display = isNew ? '' : 'none';
  });

  // ── Selector de calidad ───────────────────────────────────
  on(document, 'click', '.av-quality-item', (e, item) => {
    qsa('.av-quality-item').forEach(i => i.classList.remove('is-active'));
    item.classList.add('is-active');

    const q = item.dataset.quality;
    const target = qs('[data-bind="av-quality"]');
    if (target) target.textContent = q;
  });

  // ── Chips de movimiento personalizado ─────────────────────
  on(document, 'click', '.av-motion-chip:not(.av-motion-chip-refresh)', (e, chip) => {
    const textarea = qs('.av-motion-textarea');
    if (!textarea) return;
    const text = chip.textContent.trim();
    textarea.value = textarea.value ? `${textarea.value}, ${text}` : text;
  });

  on(document, 'click', '.av-motion-chip-refresh', () => {
    const textarea = qs('.av-motion-textarea');
    if (textarea) textarea.value = '';
  });

  // ── Modal Clóname (grabar / subir vídeo) ─────────────────
  // Estado de la grabación de cámara
  const cam = {
    stream:        null,        // MediaStream activo
    recorder:      null,        // MediaRecorder
    chunks:        [],          // Trozos de vídeo grabados
    recordedBlob:  null,        // Blob final
    countdownInt:  null,        // Intervalo del temporizador
    selectedCamId: null,
    selectedMicId: null,
    durationSec:   15,          // duración objetivo (15 s)
  };

  function openClonamodal(view = 'grabar') {
    clonamodalRoot?.classList.add('is-open');
    clonamodalRoot?.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
    switchClonamodalView(view);

    // Si abrimos en modo "grabar", solicitamos permisos de cámara
    if (view === 'grabar') requestCameraAccess();
  }

  function closeClonamodal() {
    clonamodalRoot?.classList.remove('is-open');
    clonamodalRoot?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    stopCameraStream();
  }

  function switchClonamodalView(view) {
    qsa('.cloname-view').forEach(v =>
      v.classList.toggle('is-hidden', v.dataset.clonameView !== view)
    );
  }

  // ── Cámara + grabación (WebRTC) ───────────────────────────
  function setCamState(state) {
    qsa('.cloname-state').forEach(el =>
      el.classList.toggle('is-hidden', el.dataset.camState !== state)
    );
  }

  async function requestCameraAccess() {
    // Comprobaciones previas
    if (!navigator.mediaDevices?.getUserMedia) {
      showCameraError('Navegador no compatible',
        'Tu navegador no soporta acceso a cámara. Prueba con Chrome, Safari o Firefox.',
        'Error: MediaDevicesNotSupported');
      return;
    }

    setCamState('loading');

    try {
      // Pedimos vídeo + audio. facingMode:'user' = cámara frontal (clave en móvil).
      const constraints = {
        video: cam.selectedCamId
          ? { deviceId: { exact: cam.selectedCamId } }
          : { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: cam.selectedMicId
          ? { deviceId: { exact: cam.selectedMicId } }
          : true,
      };

      stopCameraStream();   // por si había uno previo
      cam.stream = await navigator.mediaDevices.getUserMedia(constraints);

      const video = qs('#clonameVideo');
      if (video) {
        video.srcObject = cam.stream;
        await video.play().catch(() => { /* ignora autoplay errors */ });
      }
      setCamState('live');

      // Una vez con permisos podemos enumerar dispositivos con labels
      populateDeviceList();
    } catch (err) {
      handleCameraError(err);
    }
  }

  function handleCameraError(err) {
    let title = 'Dispositivo no autorizado';
    let desc  = 'Permite que el navegador acceda a tu cámara y micrófono y actualiza la página';

    switch (err?.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        title = 'Permiso denegado';
        desc  = 'Has bloqueado el acceso a la cámara. Ábrelo desde los ajustes del navegador y vuelve a intentarlo.';
        break;
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        title = 'Sin dispositivo';
        desc  = 'No hemos encontrado ninguna cámara conectada.';
        break;
      case 'NotReadableError':
      case 'TrackStartError':
        title = 'Cámara ocupada';
        desc  = 'Otra aplicación está usando tu cámara. Ciérrala y vuelve a intentarlo.';
        break;
      case 'OverconstrainedError':
        title = 'Configuración no soportada';
        desc  = 'Tu cámara no admite la configuración solicitada.';
        break;
    }
    showCameraError(title, desc, `Error: ${err?.name || 'Unknown'}`);
  }

  function showCameraError(title, desc, code) {
    const t = qs('#clonameErrorTitle');
    const d = qs('#clonameErrorDesc');
    const c = qs('#clonameErrorCode');
    if (t) t.textContent = title;
    if (d) d.innerHTML = `${desc}<br><span class="cloname-error-code" id="clonameErrorCode">${code}</span>`;
    if (c) c.textContent = code;
    setCamState('error');
  }

  function stopCameraStream() {
    if (cam.stream) {
      cam.stream.getTracks().forEach(t => t.stop());
      cam.stream = null;
    }
    if (cam.recorder && cam.recorder.state !== 'inactive') {
      try { cam.recorder.stop(); } catch { /* noop */ }
    }
    clearInterval(cam.countdownInt);
    cam.countdownInt = null;
  }

  async function populateDeviceList() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cams = devices.filter(d => d.kind === 'videoinput');
      const mics = devices.filter(d => d.kind === 'audioinput');

      fillDeviceMenu('#clonameCamMenu', '#clonameCamLabel', cams, 'cam');
      fillDeviceMenu('#clonameMicMenu', '#clonameMicLabel', mics, 'mic');
    } catch (e) {
      // silencioso: si falla, los selectores se quedan con su placeholder
    }
  }

  function fillDeviceMenu(menuSel, labelSel, devices, kind) {
    const menu  = qs(menuSel);
    const label = qs(labelSel);
    if (!menu) return;

    if (!devices.length) {
      menu.innerHTML = `<li><button class="cloname-lang-item" type="button" disabled>Sin dispositivos</button></li>`;
      if (label) label.textContent = kind === 'cam' ? 'Cámara' : 'Micrófono';
      return;
    }

    // El primero queda como activo por defecto
    const activeId = kind === 'cam' ? (cam.selectedCamId || devices[0].deviceId)
                                    : (cam.selectedMicId || devices[0].deviceId);
    if (kind === 'cam') cam.selectedCamId = activeId;
    else                cam.selectedMicId = activeId;

    menu.innerHTML = devices.map((d, i) => `
      <li>
        <button class="cloname-lang-item ${d.deviceId === activeId ? 'is-active' : ''}"
          type="button" data-device-id="${d.deviceId}" data-kind="${kind}">
          ${escapeHtml(d.label || `${kind === 'cam' ? 'Cámara' : 'Micrófono'} ${i + 1}`)}
        </button>
      </li>
    `).join('');

    if (label) {
      const active = devices.find(d => d.deviceId === activeId);
      label.textContent = (active?.label || (kind === 'cam' ? 'Cámara' : 'Micrófono'))
        .replace(/\s\(.*\)/, '');   // recorta "(0a23:abcd)" largo
    }
  }

  // Selección manual de dispositivo desde el dropdown
  on(document, 'click', '.cloname-lang-item[data-device-id]', (e, btn) => {
    const kind = btn.dataset.kind;
    const id   = btn.dataset.deviceId;
    if (kind === 'cam') cam.selectedCamId = id;
    else                cam.selectedMicId = id;
    requestCameraAccess();
  });

  // Reintentar acceso a cámara
  on(document, 'click', '[data-action="retry-camera"]', requestCameraAccess);

  // Volver a grabar
  on(document, 'click', '[data-action="retake-camera"]', () => {
    cam.recordedBlob = null;
    requestCameraAccess();
  });

  // ── Grabación con MediaRecorder ───────────────────────────
  function pickMimeType() {
    const candidates = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4',                  // Safari iOS/macOS
    ];
    return candidates.find(t => window.MediaRecorder?.isTypeSupported?.(t)) || '';
  }

  function startRecording() {
    if (!cam.stream || !window.MediaRecorder) {
      toast({ message: 'La grabación no está disponible en este dispositivo', type: 'error' });
      return;
    }
    cam.chunks = [];
    const mimeType = pickMimeType();

    try {
      cam.recorder = new MediaRecorder(cam.stream, mimeType ? { mimeType } : undefined);
    } catch (e) {
      toast({ message: 'No se pudo iniciar la grabación', type: 'error' });
      return;
    }

    cam.recorder.addEventListener('dataavailable', (ev) => {
      if (ev.data && ev.data.size > 0) cam.chunks.push(ev.data);
    });

    cam.recorder.addEventListener('stop', () => {
      const blob = new Blob(cam.chunks, { type: mimeType || 'video/webm' });
      cam.recordedBlob = blob;
      showReview(blob);
    });

    cam.recorder.start();

    // UI de grabación + temporizador descendente
    const recBtn  = qs('#clonameRecBtn');
    const recInd  = qs('#clonameRecIndicator');
    const timerEl = qs('#clonameRecTimer');
    recBtn?.classList.add('is-recording');
    recBtn?.setAttribute('aria-label', 'Detener grabación');
    recInd?.classList.remove('is-hidden');

    let remaining = cam.durationSec;
    const updateTimer = () => {
      const m = String(Math.floor(remaining / 60)).padStart(2, '0');
      const s = String(remaining % 60).padStart(2, '0');
      if (timerEl) timerEl.textContent = `${m}:${s}`;
    };
    updateTimer();

    cam.countdownInt = setInterval(() => {
      remaining--;
      updateTimer();
      if (remaining <= 0) stopRecording();
    }, 1000);
  }

  function stopRecording() {
    clearInterval(cam.countdownInt);
    cam.countdownInt = null;
    if (cam.recorder && cam.recorder.state !== 'inactive') {
      cam.recorder.stop();
    }
    const recBtn = qs('#clonameRecBtn');
    const recInd = qs('#clonameRecIndicator');
    recBtn?.classList.remove('is-recording');
    recBtn?.setAttribute('aria-label', 'Iniciar grabación');
    recInd?.classList.add('is-hidden');
  }

  function showReview(blob) {
    const url = URL.createObjectURL(blob);
    const review = qs('#clonameReview');
    if (review) {
      review.src = url;
      review.load();
    }
    // Detenemos el stream en vivo para liberar la cámara durante la revisión
    if (cam.stream) {
      cam.stream.getTracks().forEach(t => t.stop());
      cam.stream = null;
    }
    setCamState('review');
  }

  // Toggle grabar/parar al pulsar el botón circular
  on(document, 'click', '#clonameRecBtn', () => {
    const isRec = qs('#clonameRecBtn')?.classList.contains('is-recording');
    if (isRec) stopRecording();
    else       startRecording();
  });

  // Al pulsar "Listo" usamos el blob (si lo hay) para subirlo
  on(document, 'click', '.btn-cloname-done', async () => {
    if (!cam.recordedBlob) {
      toast({ message: 'Graba un vídeo antes de continuar', type: 'error' });
      return;
    }
    const btn = qs('.btn-cloname-done');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Procesando...';
    }
    toast({ message: 'Subiendo grabación de avatar…', type: 'info' });
    try {
      const file = new File([cam.recordedBlob], 'clon-avatar.webm', { type: cam.recordedBlob.type });
      const result = await uploadAttachment(file);
      addNewAvatarToGrid(result.url, result.name, true);
      toast({ message: 'Avatar clonado y seleccionado', type: 'success' });
      closeClonamodal();
    } catch (err) {
      toast({ message: `Error al subir grabación: ${err.message}`, type: 'error' });
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Listo';
      }
    }
  });

  // Helper de escape (evita XSS al pintar labels de dispositivos)
  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Abrir desde "Clóname" (vista grabar)
  on(document, 'click', '.av-action-clone', () => openClonamodal('grabar'));

  // Abrir desde "Crear un avatar" (vista subir directamente)
  on(document, 'click', '.av-action-create', () => openClonamodal('subir'));

  // Cerrar
  on(document, 'click', '[data-action="close-cloname-modal"]', closeClonamodal);
  clonamodalRoot?.addEventListener('click', (e) => {
    if (e.target === clonamodalRoot) closeClonamodal();
  });

  // "Subir grabaciones" — cambia a la vista de subida
  on(document, 'click', '[data-action="cloname-switch-upload"]', () => switchClonamodalView('subir'));

  // "Atrás" desde la vista de subida — vuelve a grabar
  on(document, 'click', '[data-action="cloname-switch-grabar"]', () => switchClonamodalView('grabar'));

  // Drop zone de vídeo
  const clonameDrop = qs('#clonamodalDrop');
  if (clonameDrop) {
    clonameDrop.addEventListener('click', (e) => {
      if (e.target.closest('.cloname-gdrive-btn') || e.target.closest('.cloname-gdrive-menu')) return;
      pickClonamodalVideo();
    });
    clonameDrop.addEventListener('dragover', (e) => {
      e.preventDefault();
      clonameDrop.classList.add('is-dragover');
    });
    clonameDrop.addEventListener('dragleave', () => clonameDrop.classList.remove('is-dragover'));
    clonameDrop.addEventListener('drop', (e) => {
      e.preventDefault();
      clonameDrop.classList.remove('is-dragover');
      const file = e.dataTransfer.files?.[0];
      if (file) {
        clonameUploadedFile = file;
        toast({ message: `Vídeo "${file.name}" cargado`, type: 'success' });
        enableCreateBtn();
      }
    });
  }

  function pickClonamodalVideo() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/mp4,video/webm,video/quicktime,video/*';
    input.addEventListener('change', (ev) => {
      const file = ev.target.files?.[0];
      if (!file) return;
      clonameUploadedFile = file;
      toast({ message: `Vídeo "${file.name}" cargado`, type: 'success' });
      enableCreateBtn();
    });
    input.click();
  }

  function enableCreateBtn() {
    const btn = qs('.btn-cloname-create');
    if (btn) btn.disabled = false;
  }

  on(document, 'click', '.btn-cloname-create', async () => {
    if (!clonameUploadedFile) return;
    const btn = qs('.btn-cloname-create');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Creando...';
    }
    toast({ message: 'Subiendo y creando avatar de vídeo…', type: 'info' });
    try {
      const result = await uploadAttachment(clonameUploadedFile);
      addNewAvatarToGrid(result.url, result.name, true);
      toast({ message: 'Avatar de vídeo creado y seleccionado', type: 'success' });
      closeClonamodal();
    } catch (err) {
      toast({ message: `Error al crear avatar: ${err.message}`, type: 'error' });
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Crear avatar';
      }
    }
  });

  // ── Modal Clóname (foto) ──────────────────────────────────
  function openCloneModal() {
    cloneModal?.classList.add('is-open');
    cloneModal?.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
  }

  function closeCloneModal() {
    cloneModal?.classList.remove('is-open');
    cloneModal?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  on(document, 'click', '.av-action-photo', openCloneModal);
  on(document, 'click', '[data-action="close-clone-modal"]', closeCloneModal);

  cloneModal?.addEventListener('click', (e) => {
    if (e.target === cloneModal) closeCloneModal();
  });

  // File picker del botón "Seleccionar fotos" y clic en la drop zone
  on(document, 'click', '[data-action="clone-pick-photos"]', () => pickClonePhotos());

  const cloneDropZone = qs('#cloneDropZone');
  if (cloneDropZone) {
    cloneDropZone.addEventListener('click', (e) => {
      if (e.target.closest('[data-action="clone-pick-photos"]')) return;
      pickClonePhotos();
    });
    cloneDropZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      cloneDropZone.classList.add('is-dragover');
    });
    cloneDropZone.addEventListener('dragleave', () => cloneDropZone.classList.remove('is-dragover'));
    cloneDropZone.addEventListener('drop', (e) => {
      e.preventDefault();
      cloneDropZone.classList.remove('is-dragover');
      const file = e.dataTransfer.files?.[0];
      if (file && file.type.startsWith('image/')) {
        handlePhotoUpload(file);
      }
    });
  }

  async function handlePhotoUpload(file) {
    toast({ message: `Subiendo foto "${file.name}"…`, type: 'info' });
    try {
      const result = await uploadAttachment(file);
      addNewAvatarToGrid(result.url, result.name, false);
      toast({ message: 'Foto de avatar subida y seleccionada', type: 'success' });
      closeCloneModal();
    } catch (err) {
      toast({ message: `Error al subir la foto: ${err.message}`, type: 'error' });
    }
  }

  function pickClonePhotos() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/heic,image/webp,image/*';
    input.addEventListener('change', (ev) => {
      const file = ev.target.files?.[0];
      if (file) handlePhotoUpload(file);
    });
    input.click();
  }

  // Escape cierra cualquier modal abierto (único listener global)
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (createVoiceModal?.classList.contains('is-open')) closeCreateVoiceModal();
    if (clonamodalRoot?.classList.contains('is-open'))  closeClonamodal();
    if (cloneModal?.classList.contains('is-open'))      closeCloneModal();
    if (audioModal?.classList.contains('is-open'))      closeAudioModal();
    if (voiceModal?.classList.contains('is-open'))      closeVoiceModal();
  });

  // ── Modal de audio ───────────────────────────────────────
  function openAudioModal(tab) {
    switchAudioTab(tab);
    audioModal?.classList.add('is-open');
    audioModal?.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
  }

  function closeAudioModal() {
    audioModal?.classList.remove('is-open');
    audioModal?.setAttribute('aria-hidden', 'true');
    // Parar grabación si estaba activa
    const btnRecord = qs('#btnRecord');
    if (btnRecord?.classList.contains('is-recording')) {
      btnRecord.classList.remove('is-recording');
    }
    document.body.style.overflow = '';
  }

  function switchAudioTab(tab) {
    const titles = { grabar: 'Grabar audio', subir: 'Subir audio' };
    if (audioModalTitle) audioModalTitle.textContent = titles[tab] || 'Audio';

    qsa('.audio-tab').forEach(t =>
      t.classList.toggle('is-active', t.dataset.audioTab === tab)
    );
    qsa('.audio-tabpanel').forEach(p =>
      p.classList.toggle('is-hidden', p.dataset.audioPanel !== tab)
    );
  }

  // Abrir desde los botones del header de guion
  on(document, 'click', '[data-action="open-audio-modal"]', (e, btn) => {
    openAudioModal(btn.dataset.audioTab || 'grabar');
  });

  // Cerrar con X
  on(document, 'click', '[data-action="close-audio-modal"]', closeAudioModal);

  // Cerrar con backdrop
  audioModal?.addEventListener('click', (e) => {
    if (e.target === audioModal) closeAudioModal();
  });

  // Cambio de pestañas dentro del modal
  on(document, 'click', '.audio-tab', (e, tab) => {
    switchAudioTab(tab.dataset.audioTab);
  });

  // Botón de grabación (toggle)
  on(document, 'click', '#btnRecord', (e, btn) => {
    const recording = btn.classList.toggle('is-recording');
    if (recording) {
      toast({ message: 'Grabando audio…', type: 'info' });
    } else {
      toast({ message: 'Grabación detenida', type: 'success' });
    }
  });

  // Drop zone de audio — clic abre file picker
  on(document, 'click', '[data-action="av-pick-audio"]', () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'audio/mp3,audio/wav,audio/*';
    input.addEventListener('change', (ev) => {
      const file = ev.target.files?.[0];
      if (!file) return;
      toast({ message: `Audio "${file.name}" cargado`, type: 'success' });
      closeAudioModal();
    });
    input.click();
  });

  // Drag & drop sobre la zona de subida
  const dropZone = qs('#audioUploadDrop');
  if (dropZone) {
    dropZone.addEventListener('click', (e) => {
      if (e.target.closest('[data-action="av-pick-audio"]')) return;
      qs('[data-action="av-pick-audio"]')?.click();
    });
    dropZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      dropZone.classList.add('is-dragover');
    });
    dropZone.addEventListener('dragleave', () => dropZone.classList.remove('is-dragover'));
    dropZone.addEventListener('drop', (e) => {
      e.preventDefault();
      dropZone.classList.remove('is-dragover');
      const file = e.dataTransfer.files?.[0];
      if (!file) return;
      toast({ message: `Audio "${file.name}" cargado`, type: 'success' });
      closeAudioModal();
    });
  }

  // ── Subida de foto (tab Foto a vídeo) ────────────────────
  on(document, 'click', '[data-action="av-pick-photo"]', () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.addEventListener('change', (ev) => {
      const file = ev.target.files?.[0];
      if (!file) return;
      toast({ message: `Foto "${file.name}" añadida`, type: 'success' });
    });
    input.click();
  });

  // ── Biblioteca de voces ───────────────────────────────────
  let selectedVoice = null;

  // Abrir
  on(document, 'click', '[data-action="open-voice-library"]', () => {
    voiceModal?.classList.add('is-open');
    voiceModal?.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
  });

  // Cerrar con botón X
  on(document, 'click', '[data-action="close-voice-library"]', closeVoiceModal);

  // Cerrar con clic en backdrop
  voiceModal?.addEventListener('click', (e) => {
    if (e.target === voiceModal) closeVoiceModal();
  });

  // Pestañas de voz (Mis voces / Biblioteca)
  on(document, 'click', '.voice-tab', (e, tab) => {
    const key = tab.dataset.voiceTab; // 'mis' | 'biblioteca'
    qsa('.voice-tab').forEach(t => t.classList.remove('is-active'));
    tab.classList.add('is-active');
    qsa('.voice-panel').forEach(p =>
      p.classList.toggle('is-hidden', p.dataset.voicePanel !== key)
    );
  });

  // Selección de una voz en la lista
  on(document, 'click', '.voice-item', (e, item) => {
    if (e.target.closest('.voice-play-btn') || e.target.closest('.voice-menu-btn')) return;

    qsa('.voice-item').forEach(i => i.classList.remove('is-selected'));
    item.classList.add('is-selected');
    selectedVoice = item.dataset.voice;

    if (selectVoiceBtn) selectVoiceBtn.disabled = false;
  });

  // Play preview de voz (placeholder)
  on(document, 'click', '.voice-play-btn', (e, btn) => {
    const item = btn.closest('.voice-item');
    const name = qs('.voice-name', item)?.textContent || 'Voz';
    const isPlaying = btn.classList.contains('is-playing');

    qsa('.voice-play-btn').forEach(b => {
      b.classList.remove('is-playing');
      qs('i', b)?.classList.replace('bi-pause-fill', 'bi-play-fill');
    });

    if (!isPlaying) {
      btn.classList.add('is-playing');
      qs('i', btn)?.classList.replace('bi-play-fill', 'bi-pause-fill');
      toast({ message: `Reproduciendo "${name}"…`, type: 'info' });
      // Simula que para después de 2 s
      setTimeout(() => {
        btn.classList.remove('is-playing');
        qs('i', btn)?.classList.replace('bi-pause-fill', 'bi-play-fill');
      }, 2000);
    }
  });

  // Búsqueda de voces (filtrado simple por nombre)
  qs('#voiceSearch')?.addEventListener('input', (e) => {
    const q = e.target.value.toLowerCase();
    qsa('.voice-item').forEach(item => {
      const name = qs('.voice-name', item)?.textContent.toLowerCase() || '';
      const tags = qs('.voice-tags', item)?.textContent.toLowerCase() || '';
      item.style.display = (name.includes(q) || tags.includes(q)) ? '' : 'none';
    });
  });

  // Confirmar selección de voz
  on(document, 'click', '[data-action="select-voice"]', () => {
    if (!selectedVoice) return;
    const name = qs(`.voice-item[data-voice="${selectedVoice}"] .voice-name`)?.textContent || selectedVoice;
    const voiceLabel = qs('[data-bind="av-voice-name"]');
    if (voiceLabel) voiceLabel.textContent = name;
    toast({ message: `Voz "${name}" seleccionada`, type: 'success' });
    closeVoiceModal();
  });

  // ── Generar ───────────────────────────────────────────────
  on(document, 'click', '[data-action="generate-avatar"]', async () => {
    const hasScript = scriptArea?.value.trim().length > 0;
    const selectedAvatarEl = qs('.av-thumb-card.is-selected');

    if (!hasScript) {
      toast({ message: 'Escribe un guion antes de generar', type: 'error' });
      return;
    }
    if (!selectedAvatarEl) {
      toast({ message: 'Selecciona un avatar del panel derecho o crea uno nuevo', type: 'error' });
      return;
    }

    const avatarId = selectedAvatarEl.dataset.avatar;
    const text = scriptArea.value.trim();

    toast({ message: 'Generando vídeo de avatar…', type: 'success' });

    try {
      const result = await apiRequest('/generate/avatar', {
        method: 'POST',
        body: JSON.stringify({
          avatarId,
          voiceId: selectedVoice || 'f8c69e517f424cafaecde32dde57096b',
          text,
        }),
      });

      if (result && result.jobId) {
        toast({ message: 'Generación iniciada con éxito. Verás el progreso aquí abajo.', type: 'success' });
        const list = qs('#avFotoGenerationsList') || qs('.av-recent-section') || qs('#avGenCount');
        if (list) {
          list.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    } catch (err) {
      toast({ message: `Error al generar avatar: ${err.message}`, type: 'error' });
    }
  });

  // ── Modal: Crear voz (grabación de audio) ─────────────────
  const cv = {
    stream:        null,
    recorder:      null,
    chunks:        [],
    recordedBlob:  null,
    countdownInt:  null,
    selectedMicId: null,
    durationSec:   30,        // duración máxima
    minDurationSec: 10,       // mínima antes de poder parar manualmente
    elapsedSec:    0,         // segundos transcurridos en la grabación actual
  };

  async function openCreateVoiceModal() {
    createVoiceModal?.classList.add('is-open');
    createVoiceModal?.removeAttribute('aria-hidden');
    cvSwitchView('create');
    cvSwitchTab('grabar');
    await requestMicAccess();
  }

  function closeCreateVoiceModal() {
    createVoiceModal?.classList.remove('is-open');
    createVoiceModal?.setAttribute('aria-hidden', 'true');
    cvStopRecording(/*silent*/ true);
    cvStopStream();
    cvCleanupReview();
    const elapsed = qs('#cvTimeElapsed');
    if (elapsed) elapsed.textContent = '00:00';
  }

  // Cambiar entre vista "create" y "review"
  function cvSwitchView(view) {
    qsa('.cv-view').forEach(v =>
      v.classList.toggle('is-hidden', v.dataset.cvView !== view)
    );
  }

  // Cambiar entre tabs "grabar" / "subir"
  function cvSwitchTab(tab) {
    qsa('.cv-pill-tab').forEach(t =>
      t.classList.toggle('is-active', t.dataset.cvTab === tab)
    );
    qsa('.cv-panel').forEach(p =>
      p.classList.toggle('is-hidden', p.dataset.cvPanel !== tab)
    );
  }

  on(document, 'click', '.cv-pill-tab', (e, btn) => cvSwitchTab(btn.dataset.cvTab));

  // Volver a la vista de creación desde revisión
  on(document, 'click', '[data-action="cv-back-to-create"]', () => {
    cvCleanupReview();
    cvSwitchView('create');
    requestMicAccess();
  });

  function cvStopStream() {
    if (cv.stream) {
      cv.stream.getTracks().forEach(t => t.stop());
      cv.stream = null;
    }
  }

  async function requestMicAccess() {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCvMicLabel('Navegador no compatible');
      toast({ message: 'Tu navegador no soporta acceso al micrófono', type: 'error' });
      return;
    }
    if (window.isSecureContext === false) {
      setCvMicLabel('Conexión no segura');
      toast({ message: 'El micrófono solo está disponible en HTTPS', type: 'error' });
      return;
    }

    setCvMicLabel('Cargando dis…');

    try {
      cvStopStream();
      cv.stream = await navigator.mediaDevices.getUserMedia({
        audio: cv.selectedMicId
          ? { deviceId: { exact: cv.selectedMicId } }
          : true,
      });
      await cvPopulateMicList();
    } catch (err) {
      handleCvMicError(err);
    }
  }

  function handleCvMicError(err) {
    let msg = 'No se pudo acceder al micrófono';
    switch (err?.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        msg = 'Permiso de micrófono denegado'; break;
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        msg = 'No hay micrófonos disponibles'; break;
      case 'NotReadableError':
        msg = 'Micrófono en uso por otra aplicación'; break;
    }
    setCvMicLabel(msg);
    toast({ message: msg, type: 'error' });
  }

  function setCvMicLabel(text) {
    const label = qs('#cvMicLabel');
    if (label) label.textContent = text;
  }

  async function cvPopulateMicList() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const mics = devices.filter(d => d.kind === 'audioinput');
      const menu = qs('#cvMicMenu');
      if (!menu) return;

      if (!mics.length) {
        menu.innerHTML = `<li><button class="cv-lang-item" type="button" disabled>Sin micrófonos</button></li>`;
        setCvMicLabel('Sin dispositivos');
        return;
      }

      const activeId = cv.selectedMicId || mics[0].deviceId;
      cv.selectedMicId = activeId;

      menu.innerHTML = mics.map((d, i) => `
        <li>
          <button class="cv-lang-item ${d.deviceId === activeId ? 'is-active' : ''}"
            type="button" data-cv-mic-id="${d.deviceId}">
            ${escapeHtml(d.label || `Micrófono ${i + 1}`)}
          </button>
        </li>
      `).join('');

      const active = mics.find(d => d.deviceId === activeId);
      setCvMicLabel((active?.label || 'Micrófono').replace(/\s\(.*\)/, ''));
    } catch (e) { /* silencioso */ }
  }

  // Selección manual de micrófono
  on(document, 'click', '[data-cv-mic-id]', (e, btn) => {
    cv.selectedMicId = btn.dataset.cvMicId;
    requestMicAccess();
  });

  // ── Grabación de audio con MediaRecorder ──────────────────
  function cvPickAudioMime() {
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',     // Safari
      'audio/ogg;codecs=opus',
    ];
    return candidates.find(t => window.MediaRecorder?.isTypeSupported?.(t)) || '';
  }

  function cvStartRecording() {
    if (!cv.stream || !window.MediaRecorder) {
      toast({ message: 'La grabación no está disponible en este dispositivo', type: 'error' });
      return;
    }
    cv.chunks = [];
    const mimeType = cvPickAudioMime();

    try {
      cv.recorder = new MediaRecorder(cv.stream, mimeType ? { mimeType } : undefined);
    } catch (e) {
      toast({ message: 'No se pudo iniciar la grabación', type: 'error' });
      return;
    }

    cv.recorder.addEventListener('dataavailable', (ev) => {
      if (ev.data && ev.data.size > 0) cv.chunks.push(ev.data);
    });
    cv.recorder.addEventListener('stop', () => {
      cv.recordedBlob = new Blob(cv.chunks, { type: mimeType || 'audio/webm' });
      cvShowReview(cv.recordedBlob);
    });

    cv.recorder.start();
    qs('#cvRecordBtn')?.classList.add('is-recording');

    // Cuenta ASCENDENTE 00:00 → 00:30
    cv.elapsedSec = 0;
    const elapsedEl = qs('#cvTimeElapsed');
    const tick = () => {
      const m = String(Math.floor(cv.elapsedSec / 60)).padStart(2, '0');
      const s = String(cv.elapsedSec % 60).padStart(2, '0');
      if (elapsedEl) elapsedEl.textContent = `${m}:${s}`;
    };
    tick();
    cv.countdownInt = setInterval(() => {
      cv.elapsedSec++;
      tick();
      if (cv.elapsedSec >= cv.durationSec) cvStopRecording(false, /*force*/ true);
    }, 1000);
  }

  // silent  → no muestra toasts ni guarda blob (al cerrar modal)
  // force   → ignora la duración mínima (cuando el temporizador llega al máximo)
  function cvStopRecording(silent = false, force = false) {
    if (!force && !silent && cv.elapsedSec < cv.minDurationSec
        && cv.recorder && cv.recorder.state === 'recording') {
      toast({
        message: `Graba al menos ${cv.minDurationSec} segundos para obtener mejores resultados`,
        type: 'warning',
        duration: 3500,
      });
      return;     // NO paramos — sigue grabando
    }

    clearInterval(cv.countdownInt);
    cv.countdownInt = null;
    if (cv.recorder && cv.recorder.state !== 'inactive') {
      try { cv.recorder.stop(); } catch { /* noop */ }
    }
    qs('#cvRecordBtn')?.classList.remove('is-recording');
    if (silent) cv.recordedBlob = null;
  }

  function openCreateVoiceModal() {
    if (!createVoiceModal) return;
    closeVoiceModal(); // Cerrar biblioteca si estaba abierta
    createVoiceModal.classList.add('is-open');
    createVoiceModal.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
    cvSwitchView('create');
    requestMicAccess();
  }

  function closeCreateVoiceModal() {
    if (!createVoiceModal) return;
    createVoiceModal.classList.remove('is-open');
    createVoiceModal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    cvStopStream();
    cvStopRecording(true);
    cvCleanupReview();
  }

  function cvSwitchView(view) {
    qsa('.cv-view').forEach(v => {
      v.classList.toggle('is-hidden', v.dataset.cvView !== view);
    });
  }

  // Pestañas Grabar / Subir en el modal de crear voz
  on(document, 'click', '[data-cv-tab]', (e, tab) => {
    const tabName = tab.dataset.cvTab;
    qsa('.cv-pill-tab').forEach(t => t.classList.remove('is-active'));
    tab.classList.add('is-active');
    qsa('[data-cv-panel]').forEach(p => p.classList.toggle('is-hidden', p.dataset.cvPanel !== tabName));
    if (tabName === 'grabar') requestMicAccess();
    else cvStopStream();
  });

  // Botón Atrás en la vista de revisión
  on(document, 'click', '[data-action="cv-back-to-create"]', () => {
    cvCleanupReview();
    cvSwitchView('create');
    requestMicAccess();
  });

  on(document, 'click', '[data-action="open-create-voice"]', openCreateVoiceModal);
  on(document, 'click', '[data-action="close-create-voice"]', closeCreateVoiceModal);

  createVoiceModal?.addEventListener('click', (e) => {
    if (e.target === createVoiceModal) closeCreateVoiceModal();
  });

  // Toggle grabar / parar
  on(document, 'click', '#cvRecordBtn', () => {
    const isRec = qs('#cvRecordBtn')?.classList.contains('is-recording');
    if (isRec) cvStopRecording();
    else       cvStartRecording();
  });

  // ── Subir audio (panel "subir") ───────────────────────────
  const cvDrop = qs('#cvUploadDrop');
  if (cvDrop) {
    cvDrop.addEventListener('click', () => cvPickAudioFile());
    cvDrop.addEventListener('dragover', (e) => {
      e.preventDefault();
      cvDrop.classList.add('is-dragover');
    });
    cvDrop.addEventListener('dragleave', () => cvDrop.classList.remove('is-dragover'));
    cvDrop.addEventListener('drop', (e) => {
      e.preventDefault();
      cvDrop.classList.remove('is-dragover');
      const file = e.dataTransfer.files?.[0];
      if (file) cvHandleUploadedFile(file);
    });
  }

  function cvPickAudioFile() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'audio/mp3,audio/wav,audio/mpeg,video/mp4,video/webm,audio/*,video/*';
    input.addEventListener('change', (ev) => {
      const file = ev.target.files?.[0];
      if (file) cvHandleUploadedFile(file);
    });
    input.click();
  }

  function cvHandleUploadedFile(file) {
    if (file.size > 2 * 1024 * 1024 * 1024) {
      toast({ message: 'El archivo supera los 2 GB', type: 'error' });
      return;
    }
    cv.recordedBlob = file;
    cvStopStream();
    cvShowReview(file);
  }

  // ── Vista de revisión + reproductor ───────────────────────
  function cvShowReview(blob) {
    const audio    = qs('#cvAudioReview');
    const playBtn  = qs('#cvPlayBtn');
    const playIcon = qs('#cvPlayBtn i');
    const cur      = qs('#cvCurrentTime');
    const tot      = qs('#cvTotalTime');
    const fill     = qs('#cvProgressFill');
    const thumb    = qs('#cvProgressThumb');
    const progress = qs('#cvProgress');
    const nameInp  = qs('#cvVoiceName');

    if (!audio) return;

    // Crea URL nueva y limpia la anterior
    if (cv.audioUrl) URL.revokeObjectURL(cv.audioUrl);
    cv.audioUrl = URL.createObjectURL(blob);
    audio.src = cv.audioUrl;
    audio.load();

    // Sugiere un nombre por defecto
    if (nameInp && !nameInp.value) {
      const stamp = new Date().toISOString().slice(0, 16).replace('T', '-');
      nameInp.value = `mi-voz-${stamp}`;
    }

    // Cuando hay duración, la mostramos
    audio.addEventListener('loadedmetadata', () => {
      if (tot) tot.textContent = fmtTime(audio.duration);
    }, { once: true });

    // Actualiza posición durante la reproducción
    audio.ontimeupdate = () => {
      const pct = (audio.currentTime / (audio.duration || 1)) * 100;
      if (fill)  fill.style.width  = `${pct}%`;
      if (thumb) thumb.style.left  = `${pct}%`;
      if (cur)   cur.textContent   = fmtTime(audio.currentTime);
    };

    audio.onended = () => {
      if (playIcon) {
        playIcon.classList.remove('bi-pause-fill');
        playIcon.classList.add('bi-play-fill');
      }
      playBtn?.setAttribute('aria-label', 'Reproducir');
    };

    // Click en la barra de progreso → seek
    if (progress && !progress.dataset.bound) {
      progress.dataset.bound = '1';
      progress.addEventListener('click', (e) => {
        const rect = progress.getBoundingClientRect();
        const ratio = (e.clientX - rect.left) / rect.width;
        if (audio.duration) audio.currentTime = ratio * audio.duration;
      });
    }

    cvSwitchView('review');
  }

  function cvCleanupReview() {
    const audio = qs('#cvAudioReview');
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
    if (cv.audioUrl) {
      URL.revokeObjectURL(cv.audioUrl);
      cv.audioUrl = null;
    }
    const playIcon = qs('#cvPlayBtn i');
    if (playIcon) {
      playIcon.classList.remove('bi-pause-fill');
      playIcon.classList.add('bi-play-fill');
    }
    const fill  = qs('#cvProgressFill');
    const thumb = qs('#cvProgressThumb');
    const cur   = qs('#cvCurrentTime');
    if (fill)  fill.style.width = '0%';
    if (thumb) thumb.style.left = '0%';
    if (cur)   cur.textContent  = '00:00';
  }

  // Play / Pause
  on(document, 'click', '#cvPlayBtn', () => {
    const audio = qs('#cvAudioReview');
    const icon  = qs('#cvPlayBtn i');
    if (!audio) return;
    if (audio.paused) {
      audio.play();
      icon?.classList.remove('bi-play-fill');
      icon?.classList.add('bi-pause-fill');
    } else {
      audio.pause();
      icon?.classList.remove('bi-pause-fill');
      icon?.classList.add('bi-play-fill');
    }
  });

  // Borrar grabación → vuelve a "create"
  on(document, 'click', '#cvTrashBtn', () => {
    cv.recordedBlob = null;
    cvCleanupReview();
    cvSwitchView('create');
    requestMicAccess();
    toast({ message: 'Grabación eliminada', type: 'info' });
  });

  // Generar voz → crea y selecciona la voz clonada
  on(document, 'click', '[data-action="cv-generate"]', () => {
    if (!cv.recordedBlob) {
      toast({ message: 'No hay grabación disponible', type: 'error' });
      return;
    }
    const name = qs('#cvVoiceName')?.value.trim() || 'Mi Voz Clonada';
    if (!name) {
      toast({ message: 'Ponle un nombre a tu voz', type: 'error' });
      qs('#cvVoiceName')?.focus();
      return;
    }

    toast({ message: `🎤 Voz "${name}" procesada y clonada con IA`, type: 'success' });

    // Agregar la voz grabada al panel "Mis voces"
    const misVocesList = qs('[data-voice-panel="mis"] .voice-list');
    if (misVocesList) {
      const audioUrl = URL.createObjectURL(cv.recordedBlob);
      const newVoiceItem = document.createElement('div');
      newVoiceItem.className = 'voice-item is-selected';
      newVoiceItem.dataset.voiceId = 'face330a542b4357aa0611779c64c12f'; // Mateo / clon para HeyGen
      newVoiceItem.dataset.voiceName = `${name} (Tu Voz)`;
      newVoiceItem.dataset.previewUrl = audioUrl;
      newVoiceItem.innerHTML = `
        <button class="voice-play-btn" type="button" aria-label="Previsualizar ${escapeHtml(name)}"><i class="bi bi-play-fill"></i></button>
        <div class="voice-info"><span class="voice-name">${escapeHtml(name)}</span><span class="voice-tags">Voz Clonada con IA, Personalizada</span></div>
        <span class="voice-badge voice-badge-public">Tu Voz</span><span class="voice-flag">🎙️</span>
      `;

      // Desseleccionar otras voces en el panel
      qsa('.voice-item').forEach(v => v.classList.remove('is-selected'));
      misVocesList.prepend(newVoiceItem);

      // Activar esta voz inmediatamente en el editor
      selectedVoiceId = 'face330a542b4357aa0611779c64c12f';
      selectedVoiceName = `${name} (Tu Voz)`;
      const labelEl = qs('[data-bind="av-voice-name"]');
      if (labelEl) labelEl.textContent = selectedVoiceName;
    }

    closeCreateVoiceModal();
    toast({ message: `✅ Voz "${name}" seleccionada para tu avatar.`, type: 'success' });
  });

  // Helper para formatear segundos a mm:ss
  function fmtTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) seconds = 0;
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  // ── Gestión del Modal de Voces y Selección de Voz ──────────
  let selectedVoiceId = 'face330a542b4357aa0611779c64c12f'; // Mateo (Masculina ES) por defecto
  let selectedVoiceName = 'Mateo';
  let activeAudioPreview = null;
  let activePlayBtn = null;
  let pendingVoiceId = null;
  let pendingVoiceName = null;

  function openVoiceModal() {
    if (!voiceModal) return;
    voiceModal.classList.add('is-open');
    voiceModal.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
  }

  function closeVoiceModal() {
    if (!voiceModal) return;
    voiceModal.classList.remove('is-open');
    voiceModal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (activeAudioPreview) {
      activeAudioPreview.pause();
      activeAudioPreview = null;
    }
    if (activePlayBtn) {
      const icon = activePlayBtn.querySelector('i');
      if (icon) icon.className = 'bi bi-play-fill';
      activePlayBtn = null;
    }
  }

  // Abrir / Cerrar modal de voces
  on(document, 'click', '[data-action="open-voice-library"]', openVoiceModal);
  on(document, 'click', '[data-action="close-voice-library"]', closeVoiceModal);
  voiceModal?.addEventListener('click', (e) => {
    if (e.target === voiceModal) closeVoiceModal();
  });

  // Cambiar entre pestañas del modal (Mis voces / Biblioteca)
  on(document, 'click', '[data-voice-tab]', (e, tab) => {
    const tabName = tab.dataset.voiceTab;
    qsa('.voice-tab').forEach(t => t.classList.remove('is-active'));
    tab.classList.add('is-active');
    qsa('[data-voice-panel]').forEach(p => p.classList.toggle('is-hidden', p.dataset.voicePanel !== tabName));
  });

  // Selección de elemento de voz en la lista
  on(document, 'click', '.voice-item', (e, item) => {
    if (e.target.closest('.voice-play-btn')) return; // se gestiona aparte
    qsa('.voice-item').forEach(v => v.classList.remove('is-selected'));
    item.classList.add('is-selected');

    pendingVoiceId = item.dataset.voiceId;
    pendingVoiceName = item.dataset.voiceName || qs('.voice-name', item)?.textContent;

    const panel = item.closest('.voice-panel');
    const selectBtn = qs('[data-action="select-voice"]', panel);
    if (selectBtn) selectBtn.disabled = false;
  });

  // Confirmar la selección de voz
  on(document, 'click', '[data-action="select-voice"]', () => {
    if (pendingVoiceId) {
      selectedVoiceId = pendingVoiceId;
      selectedVoiceName = pendingVoiceName || 'Voz seleccionada';
      const labelEl = qs('[data-bind="av-voice-name"]');
      if (labelEl) labelEl.textContent = selectedVoiceName;
      toast({ message: `Voz seleccionada: ${selectedVoiceName}`, type: 'success' });
      closeVoiceModal();
    }
  });

  // Reproducir/pausar previsualización de voz
  on(document, 'click', '.voice-play-btn', (e, btn) => {
    e.stopPropagation();
    const item = btn.closest('.voice-item');
    const previewUrl = item?.dataset.previewUrl || 'https://resource.heygen.ai/text_to_speech/WpSDQvmLGXEqXZVZQiVeg6.mp3';
    const icon = btn.querySelector('i');

    if (activeAudioPreview && activePlayBtn === btn) {
      if (activeAudioPreview.paused) {
        activeAudioPreview.play();
        if (icon) icon.className = 'bi bi-pause-fill';
      } else {
        activeAudioPreview.pause();
        if (icon) icon.className = 'bi bi-play-fill';
      }
      return;
    }

    if (activeAudioPreview) {
      activeAudioPreview.pause();
      if (activePlayBtn) {
        const prevIcon = activePlayBtn.querySelector('i');
        if (prevIcon) prevIcon.className = 'bi bi-play-fill';
      }
    }

    activeAudioPreview = new Audio(previewUrl);
    activePlayBtn = btn;

    if (icon) icon.className = 'bi bi-pause-fill';
    activeAudioPreview.play().then(() => {
      toast({ message: `Reproduciendo prueba de voz...`, type: 'info' });
    }).catch(err => {
      console.warn('Error playing voice preview:', err);
      if (icon) icon.className = 'bi bi-play-fill';
    });

    activeAudioPreview.onended = () => {
      if (icon) icon.className = 'bi bi-play-fill';
      activeAudioPreview = null;
      activePlayBtn = null;
    };
  });

  // ── Carga y renderizado de "Mis Creaciones Recientes" debajo del grid ──
  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/[&<>"']/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[m]);
  }

  function formatTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const diffSec = (Date.now() - d.getTime()) / 1000;
    if (diffSec < 60) return 'hace unos segundos';
    if (diffSec < 3600) return `hace ${Math.floor(diffSec / 60)} min`;
    if (diffSec < 86400) return `hace ${Math.floor(diffSec / 3600)} h`;
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  }

  let avatarPollIntervals = [];

  async function loadRecentAvatarGenerations() {
    const section = qs('#avRecentGenerationsSection');
    const container = qs('#avRecentGenerationsList');
    if (!container) return;

    if (section) {
      section.style.display = 'block';
      section.style.marginTop = '48px';
      section.style.marginBottom = '40px';
    }

    // Limpiar intervalos de sondeo anteriores
    avatarPollIntervals.forEach(id => clearInterval(id));
    avatarPollIntervals = [];

    try {
      const allHistory = await listHistory();
      const avatarItems = (allHistory || []).filter(item => item.type === 'avatar' || item.type === 'video' || (item.model && item.model.toLowerCase().includes('avatar')));

      if (avatarItems.length === 0) {
        container.innerHTML = `
          <div style="padding:24px; text-align:center; background:rgba(255,255,255,0.02); border:1px dashed rgba(255,255,255,0.1); border-radius:12px; color:#a1a1aa; font-size:0.85rem;">
            <i class="bi bi-collection-play" style="font-size:1.8rem; display:block; margin-bottom:8px; opacity:0.5;"></i>
            Aún no tienes creaciones de avatar recientes. ¡Escribe un guion a la izquierda y pulsa en <strong>Generar</strong>!
          </div>
        `;
        return;
      }

      container.innerHTML = avatarItems.slice(0, 6).map(item => {
        let metaObj = {};
        try {
          if (typeof item.meta === 'string') metaObj = JSON.parse(item.meta);
          else if (item.meta) metaObj = item.meta;
        } catch {}

        const photo = metaObj.photoUrl || metaObj.photo || item.url || item.thumb || '/uploads/avatar_annie.png';
        const isPending = item.status === 'queued' || item.status === 'processing';
        const isVideo = !isPending && item.url && !/\.(png|jpe?g|gif|webp)$/i.test(item.url);

        const statusBadge = isPending
          ? `<span class="av-gen-badge-status status-pending" style="background:rgba(99,102,241,0.85); color:white; padding:4px 8px; border-radius:12px; font-size:0.75rem;"><span class="spinner-border spinner-border-sm" role="status"></span> Generando...</span>`
          : `<span class="av-gen-badge-status status-ok" style="background:rgba(16,185,129,0.85); color:white; padding:4px 8px; border-radius:12px; font-size:0.75rem;"><i class="bi bi-check2-circle"></i> Completado</span>`;

        const mediaContent = isPending
          ? `<img class="av-gen-video" src="${photo}" alt="" style="width:100%; height:110px; object-fit:cover; border-radius:10px; filter: brightness(0.75);" />`
          : (isVideo
              ? `<video class="av-gen-video" src="${item.url}" poster="${photo}" loop playsinline style="width:100%; height:110px; object-fit:cover; border-radius:10px;"></video>
                 <button class="av-gen-play-btn" type="button" style="position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); background:rgba(0,0,0,0.6); border:none; color:white; border-radius:50%; width:32px; height:32px; display:flex; align-items:center; justify-content:center;"><i class="bi bi-play-fill"></i></button>`
              : `<img class="av-gen-video" src="${photo}" alt="" style="width:100%; height:110px; object-fit:cover; border-radius:10px;" />`);

        return `
          <div class="av-gen-card" data-id="${item.id}" data-job-id="${item.jobId || ''}" style="background: var(--color-bg-surface-2, #1c1c1e); border-radius:12px; padding:12px; position:relative;">
            <div class="av-gen-media" style="position:relative; margin-bottom:8px;">
              ${mediaContent}
              <div style="position:absolute; top:8px; left:8px;">${statusBadge}</div>
            </div>
            <div class="av-gen-details">
              <h4 class="av-gen-name" style="font-size:0.9rem; font-weight:600; margin:0 0 4px 0; color:white;">${escapeHtml(item.title || 'Avatar Generado')}</h4>
              <p class="av-gen-script" style="font-size:0.78rem; color: #a1a1aa; margin:0 0 8px 0; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden;">"${escapeHtml(item.prompt || '')}"</p>
              <div class="av-gen-foot" style="display:flex; align-items:center; justify-content:space-between; font-size:0.75rem; color:#71717a;">
                <span class="av-gen-date"><i class="bi bi-clock"></i> ${formatTime(item.createdAt)}</span>
                <div style="display:flex; gap:6px;">
                  <a class="av-gen-act-btn btn-dl" href="${item.url || photo}" download target="_blank" title="Descargar" style="color:white; background:rgba(255,255,255,0.1); padding:4px 8px; border-radius:6px; text-decoration:none;">
                    <i class="bi bi-download"></i>
                  </a>
                  <button class="av-gen-act-btn btn-del-avatar-gen" data-id="${item.id}" type="button" title="Eliminar" style="color:#ef4444; background:rgba(239,68,68,0.15); border:none; padding:4px 8px; border-radius:6px; cursor:pointer;">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        `;
      }).join('');

      // Evento eliminar en la lista de creaciones de avatar
      qsa('.btn-del-avatar-gen', container).forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.stopPropagation();
          const id = btn.getAttribute('data-id');
          if (id && confirm('¿Deseas eliminar esta creación de avatar?')) {
            try {
              const { deleteGeneration } = await import('./api.js');
              await deleteGeneration(id);
              btn.closest('.av-gen-card')?.remove();
              toast({ message: 'Avatar eliminado correctamente', type: 'success' });
            } catch (err) {
              console.error('Error al eliminar avatar:', err);
              toast({ message: 'Error al eliminar el avatar', type: 'error' });
            }
          }
        });
      });

      // Polling activo para elementos pendientes
      const pendingItems = avatarItems.filter(item => (item.status === 'queued' || item.status === 'processing') && item.jobId);
      if (pendingItems.length > 0) {
        const { getJobStatus } = await import('./api.js');
        pendingItems.forEach(item => {
          let attempts = 0;
          const pollId = setInterval(async () => {
            attempts++;
            try {
              const statusRes = await getJobStatus(item.jobId);
              if (statusRes.status === 'completed' || statusRes.status === 'failed' || attempts >= 10) {
                clearInterval(pollId);
                loadRecentAvatarGenerations();
              }
            } catch {
              if (attempts >= 4) {
                clearInterval(pollId);
                loadRecentAvatarGenerations();
              }
            }
          }, 3500);
          avatarPollIntervals.push(pollId);
        });
      }

    } catch (err) {
      console.error('[AVATAR] Error al cargar generaciones recientes:', err);
    }
  }

  // Carga inicial de generaciones recientes
  loadRecentAvatarGenerations();

  // ── Generar Vídeo de Avatar (Único Listener Limpio) ─────────
  on(document, 'click', '[data-action="generate-avatar"]', async (e, btn) => {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    const text = scriptArea?.value.trim();
    if (!text) {
      toast({ message: 'Escribe un guion para tu avatar antes de generar.', type: 'warning' });
      scriptArea?.focus();
      return;
    }

    const selectedCard = qs('.av-thumb-card.is-selected');
    const avatarId = selectedCard?.dataset.avatar || 'avatar_default';
    const avatarImg = qs('img, video', selectedCard)?.getAttribute('src') || '/uploads/avatar_annie.png';

    btn.disabled = true;
    toast({ message: '🚀 Generando vídeo de avatar…', type: 'info' });

    // Desplazar automáticamente hacia abajo hasta la sección de creaciones recientes
    const recentSection = qs('#avRecentGenerationsSection');
    const recentList = qs('#avRecentGenerationsList');
    if (recentSection) {
      recentSection.style.display = 'block';
      recentSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Insertar tarjeta temporal de carga inmediatamente
    if (recentList) {
      const loadingCard = document.createElement('div');
      loadingCard.id = 'tempLoadingCard';
      loadingCard.style.cssText = 'background: var(--color-bg-surface-2, #1c1c1e); border-radius:12px; padding:12px; position:relative;';
      loadingCard.innerHTML = `
        <div style="position:relative; margin-bottom:8px;">
          <img src="${avatarImg}" style="width:100%; height:160px; object-fit:cover; border-radius:10px; filter: brightness(0.75);" />
          <div style="position:absolute; top:8px; left:8px; background:rgba(99,102,241,0.9); color:white; padding:4px 8px; border-radius:12px; font-size:0.75rem; display:flex; align-items:center; gap:6px;">
            <span class="spinner-border spinner-border-sm" role="status"></span> Generando vídeo IA...
          </div>
        </div>
        <div>
          <h4 style="font-size:0.9rem; font-weight:600; margin:0 0 4px 0; color:white;">Avatar Generado</h4>
          <p style="font-size:0.78rem; color: #a1a1aa; margin:0;">"${escapeHtml(text.substring(0, 60))}..."</p>
        </div>
      `;
      // Si había aviso de sin elementos, limpiarlo
      if (recentList.querySelector('i.bi-collection-play')) recentList.innerHTML = '';
      recentList.prepend(loadingCard);
    }

    try {
      const response = await apiRequest('/generate/avatar', {
        method: 'POST',
        body: JSON.stringify({
          text,
          avatarId,
          voiceId: selectedVoiceId,
        }),
      });

      if (response && response.result && response.result.url) {
        toast({ message: '✅ ¡Vídeo de avatar generado con éxito!', type: 'success' });
      } else {
        toast({ message: '✅ Generación iniciada con éxito.', type: 'success' });
      }

      // Recargar de inmediato la sección inferior para mostrar el resultado final
      await loadRecentAvatarGenerations();

    } catch (err) {
      console.error('[AVATAR] Error al generar:', err);
      toast({ message: `Error al generar avatar: ${err.message}`, type: 'error' });
      qs('#tempLoadingCard')?.remove();
    } finally {
      btn.disabled = false;
    }
  });
} // fin de initAvatarPage
